import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Banknote, CalendarClock, GraduationCap, Target, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ModernCard,
  ModernCardBody,
  ModernCardFooter,
  ModernCardHeader,
} from '@/components/ui/modern-card';
import { EmptyStateIllustration } from '@/components/ui/empty-state-illustration';
import { useUser } from '@/hooks/useUser';
import { useDashboardKPIs, useEvolutionCA12Mois } from '@/hooks/queries/useDashboard';
import { TresorerieDashboardWidget } from '@/components/finances/TresorerieDashboardWidget';
import { TodoDashboardWidget } from '@/components/finances/TodoDashboardWidget';
import { ROLE_LABELS } from '@/lib/auth';
import { formatDA, formatDACompact, formatDateLongDZ, formatNumberDZ } from '@/lib/format';

export function DashboardPage(): JSX.Element {
  const { metadata } = useUser();
  const today = new Date();
  const { data: k, isLoading } = useDashboardKPIs();
  const { data: serie = [], isLoading: serieLoading } = useEvolutionCA12Mois();

  const totalSerie = serie.reduce((acc, p) => acc + p.ca_mensuel, 0);
  const isEmpty =
    !isLoading &&
    k != null &&
    k.ca_mois === 0 &&
    k.encours_clients === 0 &&
    k.a_payer_fournisseurs === 0 &&
    k.tresorerie_nette === 0 &&
    k.nb_opportunites_open === 0 &&
    k.nb_sessions_a_venir === 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tableau de bord</h1>
          <p className="text-sm text-muted-foreground">{formatDateLongDZ(today)}</p>
        </div>
        {metadata && <Badge variant="secondary">{ROLE_LABELS[metadata.role]}</Badge>}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyStateIllustration
          type="finances"
          title="Aucune donnée à afficher"
          description="La base est vide ou un Reset a été appliqué. Saisissez votre première opération, échéance ou opportunité — les KPI se mettront à jour automatiquement."
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild size="sm">
                <Link to="/finances/operations">Saisir une opération</Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/echeances/a-encaisser">Créer une échéance</Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/crm/liste">Créer une opportunité</Link>
              </Button>
            </div>
          }
        />
      ) : (
        <>
          {/* Sprint 6.9.18 — Widget "À traiter" (factures retard, plans retard, sessions à confirmer) */}
          <TodoDashboardWidget />

          {/* Sprint 6.9.9 — Widget Trésorerie prévisionnelle (Recouvrement / Dette formateurs / Dette fournisseurs / Net) */}
          <TresorerieDashboardWidget />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <KpiTile
              variant="kpi"
              icon={<TrendingUp className="h-4 w-4" />}
              label="CA encaissé (mois en cours)"
              value={formatDA(k?.ca_mois ?? 0)}
              hint={`Depuis le 1er ${today.toLocaleDateString('fr-DZ', { month: 'long' })}`}
            />
            {/* Sprint 6.9.10 — KpiTile "Encours clients" + "À payer fournisseurs" retirés.
                Doublon avec le widget Trésorerie prévisionnelle (au-dessus) qui lit
                recouvrement_entreprises + dettes_fournisseurs (sources de vérité Sprint 6.9.x).
                Les anciens v_dashboard_kpis.encours_clients / a_payer_fournisseurs lisent
                la table echeances (Sprint 3) qui n'est plus alimentée par le workflow nouveau. */}
            {/* Sprint 6.9.9 — "Solde groupe maintenant" / Trésorerie nette retiré du grid global.
                Remplacé par le widget Trésorerie prévisionnelle dynamique avec filtre entreprise (au-dessus). */}
            <KpiTile
              variant="opportunite"
              icon={<Target className="h-4 w-4" />}
              label="Opportunités CRM ouvertes"
              value={formatNumberDZ(k?.nb_opportunites_open ?? 0)}
              hint={
                (k?.ca_pondere_open ?? 0) > 0
                  ? `CA pondéré : ${formatDA(k?.ca_pondere_open ?? 0)}`
                  : undefined
              }
              link="/crm"
            />
            <KpiTile
              variant="session"
              icon={<GraduationCap className="h-4 w-4" />}
              label="Sessions formation à venir (30j)"
              value={formatNumberDZ(k?.nb_sessions_a_venir ?? 0)}
              hint="Planifiées / inscriptions ouvertes / complètes"
              link="/formation/sessions"
            />
          </div>

          <ModernCard variant="kpi">
            <ModernCardHeader
              icon={<CalendarClock className="h-4 w-4" />}
              title="Évolution du CA encaissé (12 derniers mois)"
              subtitle={serie.length > 0 ? `Total période : ${formatDA(totalSerie)}` : undefined}
            />
            <ModernCardBody>
              <div className="h-64 w-full">
                {serieLoading ? (
                  <Skeleton className="h-full w-full rounded-lg" />
                ) : totalSerie === 0 ? (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                    Aucun encaissement sur les 12 derniers mois.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={serie}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis
                        tickFormatter={(v: number) => formatDACompact(v)}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip formatter={(v: number) => formatDA(v)} />
                      <Bar dataKey="ca_mensuel" name="CA encaissé" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ModernCardBody>
            <ModernCardFooter>
              <span className="text-muted-foreground">
                <Banknote className="mr-1 inline h-3 w-3" />
                Données live — vue <code>v_evolution_ca_mensuel</code>
              </span>
              <Link to="/finances/operations" className="text-primary hover:underline">
                Voir les opérations →
              </Link>
            </ModernCardFooter>
          </ModernCard>
        </>
      )}
    </div>
  );
}

interface KpiTileProps {
  variant: 'formation' | 'session' | 'formateur' | 'opportunite' | 'kpi' | 'neutral';
  icon: JSX.Element;
  label: string;
  value: string;
  hint?: string;
  link?: string;
}

function KpiTile({ variant, icon, label, value, hint, link }: KpiTileProps): JSX.Element {
  const card = (
    <ModernCard variant={variant} hover={!!link}>
      <ModernCardHeader icon={icon} title={label} subtitle={hint} />
      <ModernCardBody>
        <div className="num text-2xl font-semibold">{value}</div>
      </ModernCardBody>
    </ModernCard>
  );
  if (link) {
    return (
      <Link to={link} className="block">
        {card}
      </Link>
    );
  }
  return card;
}
